import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, Save, Info, Percent, ArrowDownToLine, ArrowUpFromLine, RefreshCcw, ShoppingBag, Users } from "lucide-react";

const inp =
  "w-full bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-3 py-2 focus:border-[var(--accent)] outline-none text-sm";

/** Componente de campo de % — aceita valor em % (0-50) mas armazena 0-0.5 */
function PercentField({ label, value, onChange, icon, hint, testid }) {
  const pct = ((value || 0) * 100).toFixed(2);
  return (
    <div>
      <label className="flex items-center gap-2 text-xs mb-1 opacity-80">
        {icon}
        {label}
      </label>
      <div className="relative">
        <input
          type="number"
          step="0.01"
          min="0"
          max="50"
          value={pct}
          onChange={(e) => {
            const v = Math.max(0, Math.min(50, parseFloat(e.target.value || "0")));
            onChange(v / 100);
          }}
          className={inp + " pr-8"}
          data-testid={testid}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs opacity-60">%</span>
      </div>
      {hint && <p className="text-[11px] opacity-60 mt-1">{hint}</p>}
    </div>
  );
}

export default function AdminFees() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rates, setRates] = useState({
    bank_fee_rate: 0,
    deposit_fee_rate: 0,
    transfer_fee_rate: 0,
    withdraw_fee_rate: 0,
    sales_commission_rate: 0,
    default_affiliate_rate: 0,
  });
  const [envDefaults, setEnvDefaults] = useState({});
  const [preview, setPreview] = useState({ amount: 100 });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings/fees");
      setRates({
        bank_fee_rate: data.bank_fee_rate ?? 0,
        deposit_fee_rate: data.deposit_fee_rate ?? data.bank_fee_rate ?? 0,
        transfer_fee_rate: data.transfer_fee_rate ?? data.bank_fee_rate ?? 0,
        withdraw_fee_rate: data.withdraw_fee_rate ?? data.bank_fee_rate ?? 0,
        sales_commission_rate: data.sales_commission_rate ?? 0,
        default_affiliate_rate: data.default_affiliate_rate ?? 0,
      });
      setEnvDefaults(data.env_defaults || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar taxas");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/fees", rates);
      toast.success("Taxas atualizadas! Efeito imediato nas próximas operações.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (!confirm("Restaurar taxas ao padrão do ambiente (.env)?")) return;
    setRates({
      bank_fee_rate: envDefaults.bank_fee_rate ?? 0,
      deposit_fee_rate: envDefaults.bank_fee_rate ?? 0,
      transfer_fee_rate: envDefaults.bank_fee_rate ?? 0,
      withdraw_fee_rate: envDefaults.bank_fee_rate ?? 0,
      sales_commission_rate: envDefaults.sales_commission_rate ?? 0,
      default_affiliate_rate: envDefaults.default_affiliate_rate ?? 0,
    });
  };

  const brl = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 opacity-60">
        <Loader2 className="w-4 h-4 animate-spin" /> Carregando taxas...
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="admin-fees-page">
      <header>
        <h1 className="text-4xl font-black tracking-tight">Taxas da Plataforma</h1>
        <p className="text-sm opacity-70 mt-1">
          Configure quanto o MercadoPagu cobra em cada operação. Alterações têm efeito nas próximas transações.
        </p>
      </header>

      <div className="border border-sky-500/30 bg-sky-500/5 rounded-sm p-4 flex gap-3 text-xs">
        <Info className="w-4 h-4 shrink-0 text-sky-400 mt-0.5" />
        <div className="space-y-1 opacity-90">
          <p><b className="text-sky-300">Como funcionam as taxas:</b></p>
          <ul className="list-disc pl-4 space-y-0.5">
            <li><b>Depósito/Transferência/Saque</b> — % descontada da operação bancária, vai pro saldo da plataforma.</li>
            <li><b>Comissão de Vendas</b> — % que a plataforma retém em cada venda de infoproduto (funil).</li>
            <li><b>Afiliado (padrão)</b> — % que o <em>produtor</em> paga ao afiliado por indicação. É pago do lado do produtor, não da plataforma. Cada funil pode ter seu próprio %.</li>
          </ul>
        </div>
      </div>

      <section className="border border-[var(--border)] rounded-sm p-6 space-y-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Percent className="w-5 h-5 text-[var(--accent)]" />
          Taxas Bancárias (Depósito / Transferência / Saque)
        </h2>
        <p className="text-xs opacity-70">Você cobra em cada operação de saldo/dinheiro na plataforma.</p>

        <div className="grid gap-4 sm:grid-cols-3">
          <PercentField
            label="Depósito Pix"
            icon={<ArrowDownToLine className="w-3 h-3" />}
            value={rates.deposit_fee_rate}
            onChange={(v) => setRates((r) => ({ ...r, deposit_fee_rate: v }))}
            hint="Aplicada quando cliente credita saldo via Pix."
            testid="fee-deposit"
          />
          <PercentField
            label="Transferência interna"
            icon={<RefreshCcw className="w-3 h-3" />}
            value={rates.transfer_fee_rate}
            onChange={(v) => setRates((r) => ({ ...r, transfer_fee_rate: v }))}
            hint="Quando cliente transfere entre contas da plataforma."
            testid="fee-transfer"
          />
          <PercentField
            label="Saque"
            icon={<ArrowUpFromLine className="w-3 h-3" />}
            value={rates.withdraw_fee_rate}
            onChange={(v) => setRates((r) => ({ ...r, withdraw_fee_rate: v }))}
            hint="Quando cliente retira saldo pra conta externa."
            testid="fee-withdraw"
          />
        </div>

        <details className="text-xs opacity-70">
          <summary className="cursor-pointer">Taxa base (bank_fee_rate) — compatibilidade</summary>
          <div className="mt-2 max-w-xs">
            <PercentField
              label="Taxa bancária padrão (fallback)"
              value={rates.bank_fee_rate}
              onChange={(v) => setRates((r) => ({ ...r, bank_fee_rate: v }))}
              hint="Usada quando as taxas específicas acima não estão definidas. Mantido para compat."
              testid="fee-bank"
            />
          </div>
        </details>
      </section>

      <section className="border border-[var(--border)] rounded-sm p-6 space-y-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <ShoppingBag className="w-5 h-5 text-[var(--accent)]" />
          Vendas de Infoprodutos (Funil)
        </h2>
        <p className="text-xs opacity-70">
          % que a MercadoPagu retém em cada venda aprovada dos funis (todos os produtores).
        </p>
        <div className="max-w-xs">
          <PercentField
            label="Comissão da plataforma"
            icon={<ShoppingBag className="w-3 h-3" />}
            value={rates.sales_commission_rate}
            onChange={(v) => setRates((r) => ({ ...r, sales_commission_rate: v }))}
            hint="Ex: venda de R$ 100 com taxa 8% → produtor recebe R$ 92, plataforma R$ 8."
            testid="fee-sales"
          />
        </div>
      </section>

      <section className="border border-[var(--border)] rounded-sm p-6 space-y-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Users className="w-5 h-5 text-[var(--accent)]" />
          Afiliados
        </h2>
        <p className="text-xs opacity-70">
          Comissão padrão que o <b>produtor</b> paga ao afiliado. Cada funil pode ter o próprio %; este aqui é apenas o default sugerido.
        </p>
        <div className="max-w-xs">
          <PercentField
            label="Comissão padrão do afiliado"
            icon={<Users className="w-3 h-3" />}
            value={rates.default_affiliate_rate}
            onChange={(v) => setRates((r) => ({ ...r, default_affiliate_rate: v }))}
            hint="Ex: 30% padrão. Produtor pode alterar em cada funil."
            testid="fee-affiliate"
          />
        </div>
      </section>

      <section className="border border-[var(--border)] rounded-sm p-6 bg-[var(--bg-elev)]">
        <h3 className="font-semibold mb-3 text-sm">🧮 Simulador rápido</h3>
        <div className="flex items-center gap-3 flex-wrap text-xs">
          <label>Valor da operação:</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60">R$</span>
            <input
              type="number"
              step="1"
              min="1"
              value={preview.amount}
              onChange={(e) => setPreview({ amount: parseFloat(e.target.value || "0") })}
              className={inp + " pl-9 w-32"}
              data-testid="fee-simulator-amount"
            />
          </div>
        </div>
        <div className="grid gap-2 mt-4 text-xs sm:grid-cols-2 lg:grid-cols-4">
          {[
            { key: "deposit_fee_rate", label: "Depósito" },
            { key: "transfer_fee_rate", label: "Transferência" },
            { key: "withdraw_fee_rate", label: "Saque" },
            { key: "sales_commission_rate", label: "Venda de funil" },
          ].map((s) => {
            const fee = (preview.amount || 0) * rates[s.key];
            const net = (preview.amount || 0) - fee;
            return (
              <div key={s.key} className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-base)]">
                <div className="opacity-60 mb-1">{s.label}</div>
                <div className="text-[11px] opacity-80">Plataforma: <b className="text-[var(--accent)]">{brl(fee)}</b></div>
                <div className="text-[11px] opacity-80">Cliente recebe: <b>{brl(net)}</b></div>
              </div>
            );
          })}
        </div>
      </section>

      <div className="flex gap-2 flex-wrap sticky bottom-4 bg-[var(--bg-base)]/80 backdrop-blur-sm border border-[var(--border)] rounded-sm p-3">
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 bg-[var(--accent)] text-black rounded-sm text-sm font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
          data-testid="fee-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salvar taxas
        </button>
        <button
          onClick={load}
          className="px-4 py-2 border border-[var(--border)] rounded-sm text-sm hover:bg-[var(--bg-elev)] flex items-center gap-2"
          data-testid="fee-reload-btn"
        >
          <RefreshCcw className="w-4 h-4" /> Recarregar
        </button>
        <button
          onClick={resetToDefaults}
          className="px-4 py-2 border border-[var(--border)] rounded-sm text-sm hover:bg-[var(--bg-elev)] text-white/60"
          data-testid="fee-reset-btn"
        >
          Restaurar padrões
        </button>
      </div>
    </div>
  );
}
